import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Landing from "@/pages/Landing";
import SearchPage from "@/pages/Search";
import PropertyDetail from "@/pages/PropertyDetail";
import BookingConfirm from "@/pages/BookingConfirm";
import MyBookings from "@/pages/MyBookings";
import MyFavourites from "@/pages/MyFavourites";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import HostDashboard from "@/pages/HostDashboard";
import NewListing from "@/pages/NewListing";
import Terms from "@/pages/Terms";
import TermsHub from "@/pages/TermsHub";
import BecomeHost from "@/pages/BecomeHost";
import Verify from "@/pages/Verify";
import AdminKYC from "@/pages/AdminKYC";
import AdminAds from "@/pages/AdminAds";
import AdminGuestLookup from "@/pages/AdminGuestLookup";
import AdminMessages from "@/pages/AdminMessages";
import AdminLayout from "@/pages/AdminLayout";
import AdminListings from "@/pages/AdminListings";
import HostGuidelines from "@/pages/HostGuidelines";
import SubmitAd from "@/pages/SubmitAd";
import AdGuidelines from "@/pages/AdGuidelines";
import TrustSafety from "@/pages/TrustSafety";
import AdminBookings from "@/pages/AdminBookings";
import AdminTwilio from "@/pages/AdminTwilio";
import AdminPaymentConfig from "@/pages/AdminPaymentConfig";
import AdminPayouts from "@/pages/AdminPayouts";
import AdminManagerSubscriptions from "@/pages/AdminManagerSubscriptions";
import AdminHostCommissions from "@/pages/AdminHostCommissions";
import AdminBusinesses from "@/pages/AdminBusinesses";
import AdminTeam from "@/pages/AdminTeam";
import AdminInviteAccept from "@/pages/AdminInviteAccept";
import BusinessRegister from "@/pages/BusinessRegister";
import BusinessDashboard from "@/pages/BusinessDashboard";
import BusinessInviteAccept from "@/pages/BusinessInviteAccept";
import AdStrip from "@/components/AdStrip";
import InstallPWA from "@/components/InstallPWA";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";

// Reset scroll to top on every route change (desktop + mobile)
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Auth callback: handle emergent OAuth return anywhere in the app
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <AdStrip placement="top" />
      <Navbar />
      <main className="min-h-[70vh] pb-16">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/property/:id" element={<PropertyDetail />} />
          <Route path="/booking/:id" element={<BookingConfirm />} />
          <Route path="/my/bookings" element={<MyBookings />} />
          <Route path="/my/favourites" element={<MyFavourites />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/host" element={<BecomeHost />} />
          <Route path="/host/dashboard" element={<HostDashboard />} />
          <Route path="/host/new" element={<NewListing />} />
          <Route path="/host/guidelines" element={<HostGuidelines />} />
          <Route path="/terms" element={<TermsHub />} />
          <Route path="/terms/:kind" element={<Terms />} />
          <Route path="/verify" element={<Verify />} />
          {/* Admin hub — nested routes render inside the shared tab layout */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="listings" element={<AdminListings />} />
            <Route path="kyc" element={<AdminKYC />} />
            <Route path="ads" element={<AdminAds />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="guests" element={<AdminGuestLookup />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="twilio" element={<AdminTwilio />} />
            <Route path="payments" element={<AdminPaymentConfig />} />
            <Route path="payouts" element={<AdminPayouts />} />
            <Route path="manager-subscriptions" element={<AdminManagerSubscriptions />} />
            <Route path="hosts" element={<AdminHostCommissions />} />
            <Route path="businesses" element={<AdminBusinesses />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="terms" element={<Terms />} />
          </Route>
          <Route path="/advertise" element={<SubmitAd />} />
          <Route path="/advertise/guidelines" element={<AdGuidelines />} />
          <Route path="/trust-safety" element={<TrustSafety />} />
          {/* Public admin-invite accept flow (no admin session required) */}
          <Route path="/admin/invite/:token" element={<AdminInviteAccept />} />
          {/* EasyStay Business — parallel corporate track */}
          <Route path="/business/register" element={<BusinessRegister />} />
          <Route path="/business/dashboard" element={<BusinessDashboard />} />
          <Route path="/business/invite/:token" element={<BusinessInviteAccept />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Landing />} />
        </Routes>
      </main>
      <Footer />
      <AdStrip placement="bottom" />
      <InstallPWA />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-sand">
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
            <Toaster position="top-right" richColors />
            <AppRouter />
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
